import { useCallback, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { format } from 'date-fns';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useSnackbar } from 'src/components/snackbar';
import CustomMultiSelect from 'src/components/custom-multiselect';
import FormProvider, { RHFTextField, RHFSelect, RHFCheckbox, RHFMultiCheckbox, RHFAutocomplete } from 'src/components/hook-form';
import CustomPauseUnpauseDateSelect from 'src/components/custom-pause-unpause-date-select';
import {
  updateSubscription,
  createSubscription,
  useGetRestaurantPlanGroups,
  useGetRestaurantPlanGroup,
  getSubscriptionById,
  useGetRestaurant,
  useGetRestaurantMealTagPlanTagPairs,
  useGetStartDate,
  pauseSubscription,
  unpauseSubscription,
  useGetCurrentDateTime
} from 'src/api/restaurant';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { components } from 'src/types/schema';
import { useAuthContext } from 'src/auth/hooks';
import { hasPermissions } from 'src/auth/guard/utils';
import Iconify from 'src/components/iconify';
import { PAYMENT_SUCCESS_URL, PAYMENT_ERROR_URL } from 'src/config-global';
import { generateEasy4DigitPassword } from 'src/utils/easy-password';
import { buildUrlWithEncryptedPayload } from 'src/utils/encryption';
import PauseDatesSummary from './pause-dates-summary';

// ----------------------------------------------------------------------

// Define separate types for create and update operations
type SubscriptionCreateFormValues = {
  // Restaurant Selection
  restaurant: {
    id: number;
  };
  // Customer Info
  customer: {
    id?: number;
    name: string;
    phone_number: string;
    area: {
      id: number;
    };
    block: string;
    street: string;
    house: string;
    avenue?: string | null;
    comment?: string | null;
  };
  // Plan Selection
  restaurant_plan_group: {
    id: number;
  };
  restaurant_plan: {
    id: number;
  };
  delivery_time: {
    id: number;
  };
  // Meal Sizes
  meal_sizes: {
    protein_size?: number | null;
    carb_size?: number | null;
  };
  // Meals - Updated to use meal tag and plan tag
  meals: Array<{
    id?: number;
    meal_tag: { id: number };
    plan_tag: { id: number }; // Changed from plan to plan_tag
    is_deleted?: boolean;
  }>;
  // Subscription Settings
  start_date: string;
  price: string;
  restaurant_note?: string | null;
  delivery_note?: string | null;
  off_days: number[];
  period: number;
};

type SubscriptionUpdateFormValues = {
  delivery_time: {
    id: number;
  };
  // Meal Sizes
  meal_sizes: {
    id?: number;
    protein_size?: number | null;
    carb_size?: number | null;
  };
  // Meals - Updated to use meal tag and plan tag
  meals: Array<{
    id?: number;
    meal_tag: {
      id: number;
    };
    plan_tag: { id: number }; // Changed from plan to plan_tag
    is_deleted?: boolean;
  }>;
  // Subscription Settings
  start_date: string;
  restaurant_note?: string | null;
  delivery_note?: string | null;
  extend_by_days: number;
  is_paid_update_request: boolean;

  // Read-only reference fields for the form
  restaurant: {
    id: number;
  };
  restaurant_plan_group: {
    id: number;
  };
  restaurant_plan: {
    id: number;
  };
  customer: {
    id?: number;
    name: string;
    phone_number: string;
    area: {
      id: number;
    };
    block: string;
    street: string;
    house: string;
    avenue?: string | null;
    comment?: string | null;
  };
  off_days: number[];
  period: number;
  price: string;
};

type FormValues = SubscriptionCreateFormValues | SubscriptionUpdateFormValues;

type Props = {
  id?: string;
  restaurantId: number;
};

const phoneRegExp = /^[0-9]{8}$/;

const CreateSubscriptionSchema = Yup.object().shape({
  restaurant: Yup.object().shape({
    id: Yup.number().min(1, 'Restaurant is required'),
  }),
  customer: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    phone_number: Yup.string()
      .required('Phone number is required')
      .matches(phoneRegExp, 'Invalid phone number')
      .min(8, 'Phone number is too short')
      .max(8, 'Phone number is too long'),
    area: Yup.object().shape({
      id: Yup.number().min(1, 'Area is required'),
    }),
    block: Yup.string().required('Block is required'),
    street: Yup.string().required('Street is required'),
    house: Yup.string().required('House is required'),
    avenue: Yup.string().nullable(),
    comment: Yup.string().nullable(),
  }),
  restaurant_plan_group: Yup.object().shape({
    id: Yup.number().min(1, 'Plan group is required'),
  }),
  restaurant_plan: Yup.object().shape({
    id: Yup.number().min(1, 'Plan is required'),
  }),
  delivery_time: Yup.object().shape({
    id: Yup.number().min(1, 'Delivery time is required'),
  }),
  meal_sizes: Yup.object().shape({
    protein_size: Yup.number().required('Protein size is required').nullable(),
    carb_size: Yup.number().required('Carb size is required').nullable(),
  }),
  meals: Yup.array()
    .of(
      Yup.object().shape({
        meal_tag: Yup.object().shape({
          id: Yup.number().required('Meal tag is required'),
        }),
        plan_tag: Yup.object().shape({
          id: Yup.number().required('Plan tag is required'),
        }),
        is_deleted: Yup.boolean(),
      })
    )
    // This test checks that there's at least one meal with is_deleted=false
    .test(
      'has-active-meal',
      'At least one meal tag is required',
      (meals) => meals && meals.some(meal => meal && !meal.is_deleted)
    ),
  start_date: Yup.string().required('Start date is required'),
  price: Yup.string().required('Price is required'),
  period: Yup.number()
    .required('Period is required')
    .min(1, 'Period must be at least 1 day')
    .max(90, 'Period cannot exceed 90 days'),
  restaurant_note: Yup.string().nullable(),
  delivery_note: Yup.string().nullable(),
  off_days: Yup.array().of(Yup.number()),
});

const UpdateSubscriptionSchema = Yup.object().shape({
  delivery_time: Yup.object().shape({
    id: Yup.number().min(1, 'Delivery time is required'),
  }),
  meal_sizes: Yup.object().shape({
    protein_size: Yup.number().required('Protein size is required').nullable(),
    carb_size: Yup.number().required('Carb size is required').nullable(),
  }),
  meals: Yup.array()
    .of(
      Yup.object().shape({
        meal_tag: Yup.object().shape({
          id: Yup.number().required('Meal tag is required'),
        }),
        plan_tag: Yup.object().shape({
          id: Yup.number().required('Plan tag is required'),
        }),
        is_deleted: Yup.boolean(),
      })
    )
    // This test checks that there's at least one meal with is_deleted=false
    .test(
      'has-active-meal',
      'At least one meal tag is required',
      (meals) => meals && meals.some(meal => meal && !meal.is_deleted)
    ),
  start_date: Yup.string().required('Start date is required'),
  restaurant_note: Yup.string().nullable(),
  delivery_note: Yup.string().nullable(),
  off_days: Yup.array().of(Yup.number()),
  extend_by_days: Yup.number()
    .required('Extension days is required for updates')
    .min(0, 'Extension days must be at least 0')
    .max(7, 'Extension days cannot exceed 7'),
  is_paid_update_request: Yup.boolean().required('Required for updates'),
  // Price is conditionally required when payment is requested
  // and must be greater than the original price
  price: Yup.string().when('is_paid_update_request', {
    is: true,
    then: (schema) => schema
      .required('Price is required when requesting payment')
      .test(
        'is-greater-than-original',
        'New price must be greater than the original price',
        function priceGreaterThanOriginal(value) {
          if (!value) return false;

          // Get the initial form values from when the form was created
          // This will contain the original price from the backend
          const initialValues = this.options.context?.initialValues;
          const originalPrice = initialValues?.price;

          if (!originalPrice) return true; // If we don't have the original price, skip this validation

          return parseFloat(value) > parseFloat(originalPrice);
        }
      ),
    otherwise: () => Yup.string().nullable(),
  }),

  // Read-only reference fields - no validation required as they cannot be changed
  restaurant: Yup.object(),
  restaurant_plan_group: Yup.object(),
  restaurant_plan: Yup.object(),
  customer: Yup.object(),
  period: Yup.number(),
});

export default function SubscriptionEdit({ id, restaurantId }: Props) {
  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const { mealPlanPairs, mealPlanPairsLoading } = useGetRestaurantMealTagPlanTagPairs(restaurantId);
  const isSuperUser = hasPermissions(user,['partners:misc:handle_multiple_restaurants']);

  const { restaurantPlanGroups, restaurantPlanGroupsLoading } = useGetRestaurantPlanGroups(
    restaurantId ? restaurantId.toString() : undefined,
    ''
  );

  const { restaurant, restaurantLoading } = useGetRestaurant(restaurantId || 0);
  const cities = restaurant?.areas;
  const citiesLoading = restaurantLoading;

  const [selectedPlanGroupId, setSelectedPlanGroupId] = useState<number | null>(null);
  const { restaurantPlanGroup, restaurantPlanGroupLoading } =
    useGetRestaurantPlanGroup(selectedPlanGroupId || 0);

  const [deliveryTimes, setDeliveryTimes] = useState<Array<{id: number, start_time: string, end_time: string}>>([]);
  const [deliveryTimesLoading, setDeliveryTimesLoading] = useState<boolean>(false);

  // Update state for packages using the correct type from schema.d.ts
  const [availablePackages, setAvailablePackages] = useState<NonNullable<(typeof restaurantPlanGroup)>['plans'][number]['packages']>([]);
  const [selectedPackage, setSelectedPackage] = useState<typeof availablePackages[number] | null>();
  const [packagesLoading, setPackagesLoading] = useState<boolean>(false);
  const [, setForceUpdate] = useState<number>(0);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [paymentLink, setPaymentLink] = useState<string>('');
  const [showPauseModal, setShowPauseModal] = useState<boolean>(false);
  const [pauseModalType, setPauseModalType] = useState<'pause' | 'unpause'>('pause');
  const [pauseStartDate, setPauseStartDate] = useState<string>('');
  const [pauseEndDate, setPauseEndDate] = useState<string>('');
  const [unpauseFromDate, setUnpauseFromDate] = useState<string>('');
  const [pauseStartDateError, setPauseStartDateError] = useState<string>('');
  const [overrideClient, setOverrideClient] = useState<boolean>(false);
  const [pauseLoading, setPauseLoading] = useState<boolean>(false);
  const [subscriptionData, setSubscriptionData] = useState<components["schemas"]["PartnersSubscriptionRetrieve"] | null>(null);
  const { currentDateTime, currentDateTimeLoading } = useGetCurrentDateTime();

  const isEdit = Boolean(id);

  // Default values for create mode
  const defaultCreateValues: SubscriptionCreateFormValues = {
    restaurant: {
      id: restaurantId || 0,
    },
    customer: {
      name: '',
      phone_number: '',
      area: {
        id: 0,
      },
      block: '',
      street: '',
      house: '',
      avenue: '',
      comment: '',
    },
    restaurant_plan_group: {
      id: 0,
    },
    restaurant_plan: {
      id: 0,
    },
    delivery_time: {
      id: 0,
    },
    meal_sizes: {
      protein_size: null,
      carb_size: null,
    },
    meals: [],
    start_date: moment().add(1, 'day').format('YYYY-MM-DD'),
    price: '',
    restaurant_note: '',
    delivery_note: '',
    off_days: [],
    period: 30, // Default to 30 days
  };

  // Default values for update mode
  const defaultUpdateValues: SubscriptionUpdateFormValues = {
    restaurant: {
      id: restaurantId || 0,
    },
    customer: {
      name: '',
      phone_number: '',
      area: {
        id: 0,
      },
      block: '',
      street: '',
      house: '',
      avenue: '',
      comment: '',
    },
    restaurant_plan_group: {
      id: 0,
    },
    restaurant_plan: {
      id: 0,
    },
    delivery_time: {
      id: 0,
    },
    meal_sizes: {
      protein_size: null,
      carb_size: null,
    },
    meals: [],
    start_date: moment().add(1, 'day').format('YYYY-MM-DD'),
    price: '',
    restaurant_note: '',
    delivery_note: '',
    off_days: [],
    period: 30,
    extend_by_days: 0, // Default to 1 day extension
    is_paid_update_request: false,
  };

  // Use the appropriate schema and default values based on mode
  // Explicitly type the resolvers to fix type mismatch
  const methods = useForm<FormValues>({
    resolver: isEdit
      ? yupResolver(UpdateSubscriptionSchema as any, {
          context: {
            initialValues: defaultUpdateValues  // Pass the initial values as context
          }
        })
      : yupResolver(CreateSubscriptionSchema as any),
    defaultValues: isEdit ? defaultUpdateValues : defaultCreateValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isDirty },
    watch,
    setValue,
    getValues,
  } = methods;

  const watchRestaurantId = restaurantId;
  const watchPlanGroupId = watch('restaurant_plan_group.id');
  const watchPlanId = watch('restaurant_plan.id');

  // Update selected restaurant when it changes in the form
  useEffect(() => {
    if (watchRestaurantId && watchRestaurantId !== 0) {
      // Reset dependent fields when restaurant changes
      setValue('restaurant_plan_group.id', 0);
      setValue('restaurant_plan.id', 0);
      setValue('delivery_time.id', 0);
      setSelectedPlanGroupId(null);
    }
  }, [watchRestaurantId, setValue]);

  // Auto-populate meal tags when plan is selected
  useEffect(() => {
    if (watchPlanId && watchPlanId !== 0 && restaurantPlanGroup && !isEdit) {
      try {
        // Find the selected plan
        const selectedPlan = restaurantPlanGroup.plans?.find(p => p.id === watchPlanId);

        if (selectedPlan && selectedPlan.meals && selectedPlan.meals.length > 0) {
          // Extract meal tag IDs from the plan
          const mealTagIds = selectedPlan.meals.map(meal => meal.id);

          if (mealTagIds.length > 0) {
            // Wait for mealPlanPairs to be loaded
            if (!mealPlanPairsLoading && mealPlanPairs.length > 0) {
              // Create new meal objects for each tag with proper plan_tag
              const newMeals = mealTagIds.map((mealTagId) => {
                // Find the corresponding plan_tag for this meal_tag from mealPlanPairs
                const foundPair = mealPlanPairs.find((p) => p.meal_tag.id === mealTagId);

                return {
                  meal_tag: { id: mealTagId },
                  // Use the paired plan_tag if found, otherwise fallback to the selected plan
                  plan_tag: foundPair ? { id: foundPair.plan_tag.id } : { id: watchPlanId },
                  is_deleted: false,
                };
              });

              // Set the meals in the form
              setValue('meals', newMeals);
            } else {
              console.log('Waiting for meal-plan pairs to load before auto-populating meals');
            }
          }
        }
      } catch (error) {
        console.error('Failed to auto-populate meals:', error);
      }
    }
  }, [watchPlanId, restaurantPlanGroup, setValue, isEdit, mealPlanPairs, mealPlanPairsLoading]);

  // Update selected plan group when it changes in the form
  useEffect(() => {
    if (watchPlanGroupId && watchPlanGroupId !== 0) {
      setSelectedPlanGroupId(watchPlanGroupId);

      // Reset dependent fields when plan group changes
      setValue('restaurant_plan.id', 0);
    }
  }, [watchPlanGroupId, setValue]);

  // Set meal size defaults from selected plan group
  useEffect(() => {
    if (restaurantPlanGroup && restaurantPlanGroup.protein_size && restaurantPlanGroup.carb_size) {
      setValue('meal_sizes.protein_size', restaurantPlanGroup.protein_size);
      setValue('meal_sizes.carb_size', restaurantPlanGroup.carb_size);
    }
  }, [restaurantPlanGroup, setValue]);

  // Fetch delivery times for the restaurant
  useEffect(() => {
    const handleDeliveryTimes = () => {
      if (!restaurantId || !restaurant) return;

      setDeliveryTimesLoading(true);
      try {
        // Use delivery times from the restaurant data
        if (restaurant?.delivery_times?.length > 0) {
          // Convert to our expected format
          const times = restaurant.delivery_times.map(dt => ({
            id: dt.id || 0,
            start_time: dt.start_time,
            end_time: dt.end_time,
          }));
          setDeliveryTimes(times);

          // If in edit mode and we have a delivery time ID from the form but no times selected yet
          const currentDeliveryTimeId = getValues('delivery_time.id');
          if (isEdit && currentDeliveryTimeId && currentDeliveryTimeId !== 0) {
            // Ensure the delivery time is selected in the form
            const matchingTime = times.find(t => t.id === currentDeliveryTimeId);
            if (matchingTime) {
              setValue('delivery_time.id', matchingTime.id);
            }
          }
        } else {
          setDeliveryTimes([]);
          enqueueSnackbar('No delivery times found for this restaurant', { variant: 'warning' });
        }
      } catch (error) {
        console.error('Failed to process delivery times:', error);
        enqueueSnackbar('Failed to load delivery times', { variant: 'error' });
      } finally {
        setDeliveryTimesLoading(false);
      }
    };

    handleDeliveryTimes();
  }, [restaurantId, restaurant, enqueueSnackbar, isEdit, getValues, setValue]);

  // Effect to fetch packages when restaurant plan is selected
  useEffect(() => {
    const fetchPackages = async () => {
      if (!watchPlanId || watchPlanId === 0 || isEdit) return;

      try {
        setPackagesLoading(true);

        // If restaurant plan group and plan are loaded
        if (restaurantPlanGroup && watchPlanId) {
          const plan = restaurantPlanGroup.plans?.find(p => p.id === watchPlanId);

          // Check if plan has package options
          if (plan && plan.packages && plan.packages.length > 0) {
            // Use packages directly from plan - don't add non-existent properties
            setAvailablePackages(plan.packages);

            // Auto-select package with most days
            if (plan.packages.length > 0) {
              const maxDaysPackage = plan.packages.reduce((prev, current) =>
                (prev.period > current.period) ? prev : current
              );
              setSelectedPackage(maxDaysPackage);

              // Auto-fill form with the selected package values
              setValue('period', maxDaysPackage.period);
              setValue('price', maxDaysPackage.discounted_price?.toString() || maxDaysPackage.price?.toString() || '');
              if (maxDaysPackage.off_days && maxDaysPackage.off_days.length > 0) {
                const offDayIds = maxDaysPackage.off_days.map(day => day.id);
                setValue('off_days', offDayIds);
              }
            }
          } else {
            setAvailablePackages([]);
            setSelectedPackage(null);
          }
        }
      } catch (error) {
        console.error("Error fetching packages:", error);
        enqueueSnackbar('Failed to load package options', { variant: 'error' });
      } finally {
        setPackagesLoading(false);
      }
    };

    fetchPackages();
  }, [watchPlanId, restaurantPlanGroup, isEdit, setValue, enqueueSnackbar]);

  // Handler for manual package selection
  const handlePackageChange = (newPackage: typeof availablePackages[number] | null) => {
    setSelectedPackage(newPackage);

    if (newPackage) {
      // Update form values based on the selected package
      setValue('period', newPackage.period);
      setValue('price', newPackage.price?.toString() || '');

      // Auto-select off days if the package has them
      if (newPackage.off_days && newPackage.off_days.length > 0) {
        const offDayIds = newPackage.off_days.map((day: { id: number }) => day.id);
        setValue('off_days', offDayIds);
      }
    }
  };

  // Define types for meal-plan pair selection
  type MealPlanPair = {
    mealTagId: number;
    planTagId: number;
  };

  // interface MealPlanOption {
  //   id: string;
  //   mealTagId: number;
  //   planTagId: number;
  //   mealTagName: string;
  //   planTagName: string;
  // }

  type MealWithPlanTag = {
    id?: number;
    meal_tag: { id: number };
    plan_tag: { id: number };
    is_deleted?: boolean;
  };

  // Handle meal tag and plan tag selection
  const handleMealTagChange = (selectedPairs: MealPlanPair[]) => {
    // Get current meals from the form
    const currentMeals = getValues('meals') || [];

    // Create two separate arrays to track what we need to keep or add
    const mealsToKeep: MealWithPlanTag[] = [];

    // First, handle existing meals
    currentMeals.forEach(meal => {
      // Check if this meal's combination exists in the selected pairs
      const pairExists = selectedPairs.some(
        (pair: MealPlanPair) => pair.mealTagId === meal.meal_tag.id && pair.planTagId === meal.plan_tag.id
      );

      if (pairExists) {
        // Keep this meal and ensure it's not marked as deleted
        mealsToKeep.push({
          ...meal,
          is_deleted: false
        });
      } else if (meal.id) {
        // Only keep DB meals with IDs, but mark them as deleted
        mealsToKeep.push({
          ...meal,
          is_deleted: true
        });
      }
      // Locally added meals that are no longer selected are simply not included
    });

    // Find pairs that need to be added as new meals
    const existingPairs = mealsToKeep
      .filter(meal => !meal.is_deleted)
      .map(meal => ({
        mealTagId: meal.meal_tag.id,
        planTagId: meal.plan_tag.id
      }));

    const pairsToAdd = selectedPairs.filter(
      (pair: MealPlanPair) => !existingPairs.some(
        existing => existing.mealTagId === pair.mealTagId && existing.planTagId === pair.planTagId
      )
    );

    // Add new meals for the missing pairs
    const newMeals = pairsToAdd.map((pair: MealPlanPair) => ({
      meal_tag: { id: pair.mealTagId },
      plan_tag: { id: pair.planTagId },
      is_deleted: false
    }));

    // Combine both arrays for the final result
    const updatedMeals = [...mealsToKeep, ...newMeals];

    // Use setValue with shouldDirty: true to properly mark the form as dirty
    setValue('meals', updatedMeals, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true
    });

    // Force re-render by triggering a state update
    setForceUpdate(prev => prev + 1);

    // Log for debugging
    console.log('Updated meals:', updatedMeals);
  };

  // Handler specifically for creating subscriptions
  const handleCreateSubmit = useCallback(
    async (data: SubscriptionCreateFormValues) => {
      try {
        setLoading(true);

        // Generate an easy 4-digit password for the new user
        const generatedPassword = generateEasy4DigitPassword();

        // Prepare encrypted payload for redirect URLs
        const payloadKV = {
          amount: data.price,
          deliveryDate: data.start_date,
          phone_number: data.customer.phone_number,
          password: generatedPassword,
          name: data.customer.name,
        };
        const successUrlWithPayload = await buildUrlWithEncryptedPayload(PAYMENT_SUCCESS_URL, payloadKV);
        const errorUrlWithPayload = await buildUrlWithEncryptedPayload(PAYMENT_ERROR_URL, payloadKV);

        // Format data for the create API, ensuring customer structure matches schema
        const formattedData: Omit<
          components['schemas']['PartnersSubscriptionCreateRequest'],
          'language'
        > = {
          restaurant: {
            id: restaurantId,
          },
          restaurant_plan_group: {
            id: data.restaurant_plan_group.id,
          },
          restaurant_plan: {
            id: data.restaurant_plan.id,
          },
          delivery_time: {
            id: data.delivery_time.id,
          },
          meal_sizes: data.meal_sizes,
          meals: (data.meals || []),
          start_date: data.start_date,
          price: data.price,
          period: data.period,
          restaurant_note: data.restaurant_note,
          delivery_note: data.delivery_note,
          off_days: data.off_days.map((dayId) => ({ id: dayId })),
          // Add success_url and error_url from environment variables
          success_url: successUrlWithPayload,
          error_url: errorUrlWithPayload,
          // Correctly structure customer data according to schema
          customer: {
            name: data.customer.name,
            area: { id: data.customer.area.id },
            block: data.customer.block,
            street: data.customer.street,
            house: data.customer.house,
            avenue: data.customer.avenue,
            comment: data.customer.comment,
            // The schema expects user.phone_number within the customer object
            user: {
              phone_number: data.customer.phone_number,
              password: generatedPassword,
            },
          },
        };

        // Remove comment if user is not a superuser
        if (!isSuperUser && formattedData.customer) {
          delete formattedData.customer.comment;
        }

        // we expect a type error here because we omitted certain fields (error_url, success_url, language)
        // @ts-ignore
        const response = await createSubscription(formattedData);

        // Check if payment link exists in the response
        if (response && response.current_payment_link) {
          setPaymentLink(response.current_payment_link);
          enqueueSnackbar('Subscription created successfully!', { variant: 'success' });
          setShowPaymentModal(true);
        } else {
          enqueueSnackbar('Subscription created successfully!', { variant: 'success' });
          router.push(paths.dashboard.restaurant.subscription.subscriptions);
        }
      } catch (error) {
        console.error('Failed to create subscription:', error);
        enqueueSnackbar(error.message || 'Failed to create subscription', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar, restaurantId, router, isSuperUser]
  );

  // Handler specifically for updating subscriptions
  const handleUpdateSubmit = useCallback(
    async (data: SubscriptionUpdateFormValues) => {
      try {
        if (!id) {
          throw new Error('Subscription ID is required for updates');
        }

        setLoading(true);

        // Format data for the update API
        const formattedData: Partial<
          components['schemas']['PartnersSubscriptionCreateRequest'] & {
            extend_by_days: number;
            is_paid_update_request: boolean;
          }
        > = {
          delivery_time: {
            id: data.delivery_time.id,
          },
          meal_sizes: data.meal_sizes,
          meals: data.meals
            // Only include meals that:
            // 1. Don't have an ID (new meals added during update)
            // 2. Or have an ID but are marked for deletion
            .filter((meal) => !meal.id || meal.is_deleted),
          start_date: data.start_date,
          restaurant_note: data.restaurant_note,
          delivery_note: data.delivery_note,
          off_days: data.off_days.map((dayId) => ({ id: dayId })),
          extend_by_days: data.extend_by_days,
          is_paid_update_request: data.is_paid_update_request,
        };

        // Add success_url, error_url and price when payment is requested
        if (data.is_paid_update_request) {
          const payloadKV = {
            name: data.customer.name,
            amount: data.price,
            deliveryDate: data.start_date,
            phone_number: data.customer.phone_number,
          };
          const successUrlWithPayload = await buildUrlWithEncryptedPayload(PAYMENT_SUCCESS_URL, payloadKV);
          const errorUrlWithPayload = await buildUrlWithEncryptedPayload(PAYMENT_ERROR_URL, payloadKV);
          formattedData.success_url = successUrlWithPayload;
          formattedData.error_url = errorUrlWithPayload;
          formattedData.price = data.price;
        }

        const response = await updateSubscription(id, formattedData);

        const responseWithPaymentLink = response as typeof response & { current_payment_link?: string };

        // Check if payment request was made and payment link exists in the response
        if (data.is_paid_update_request && responseWithPaymentLink && responseWithPaymentLink.current_payment_link) {
          setPaymentLink(responseWithPaymentLink.current_payment_link);
          enqueueSnackbar('Subscription updated successfully!', { variant: 'success' });
          setShowPaymentModal(true);
        } else {
          enqueueSnackbar('Subscription updated successfully!', { variant: 'success' });
          router.push(paths.dashboard.restaurant.subscription.subscriptions);
        }
      } catch (error) {
        console.error('Failed to update subscription:', error);
        enqueueSnackbar(error.message || 'Failed to update subscription', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar, id, router]
  );

  // Load subscription data when in edit mode
  useEffect(() => {
    const loadSubscriptionData = async () => {
      if (isEdit && id) {
        try {
          const data = await getSubscriptionById(id);
          setSubscriptionData(data);
        } catch (error) {
          console.error('Failed to load subscription data:', error);
          enqueueSnackbar('Failed to load subscription data', { variant: 'error' });
        }
      }
    };

    loadSubscriptionData();
  }, [isEdit, id, enqueueSnackbar]);

  // Main submit handler that delegates to the appropriate handler
  const onSubmit = useCallback(
    async (data: FormValues) => {
      if (isEdit) {
        await handleUpdateSubmit(data as SubscriptionUpdateFormValues);
      } else {
        await handleCreateSubmit(data as SubscriptionCreateFormValues);
      }
    },
    [isEdit, handleCreateSubmit, handleUpdateSubmit]
  );

  // Pause subscription handlers
  const handleOpenPauseModal = useCallback((type: 'pause' | 'unpause') => {
    setPauseModalType(type);
    
    // Set initial date based on type
    if (type === 'pause') {
      // Reset dates and errors
      setPauseStartDate('');
      setPauseEndDate('');
      setPauseStartDateError('');
    } else {
      // For unpause, reset to empty - user must select from dropdown
      setUnpauseFromDate('');
      setOverrideClient(false);
    }
    
    setShowPauseModal(true);
  }, [subscriptionData]);

  const handleClosePauseModal = useCallback(() => {
    setShowPauseModal(false);
    setPauseStartDate('');
    setPauseEndDate('');
    setUnpauseFromDate('');
    setOverrideClient(false);
    setPauseStartDateError('');
  }, []);

  const handleConfirmPause = useCallback(async () => {
    if (!id) return;

    // Validate required fields based on modal type
    if (pauseModalType === 'pause') {
      if (!pauseStartDate) {
        setPauseStartDateError('Start date is required');
        return;
      }
      if (!pauseEndDate) return;
      setPauseStartDateError('');
    }
    if (pauseModalType === 'unpause' && !unpauseFromDate) return;

    try {
      setPauseLoading(true);

      if (pauseModalType === 'pause') {
        const pauseData: components["schemas"]["PartnersSubscriptionPauseRequest"] = {
          start_date: pauseStartDate,
          end_date: pauseEndDate,
          adhere_to_pause_limit: false, // Set to false as requested
        };

        await pauseSubscription(id, pauseData);
      } else {
        const unpauseData: components["schemas"]["PartnersSubscriptionUnpauseRequest"] = {
          from_date: unpauseFromDate,
          override_client: overrideClient,
        };

        await unpauseSubscription(id, unpauseData);
      }
      
      enqueueSnackbar(
        pauseModalType === 'pause' 
          ? 'Subscription paused successfully!' 
          : 'Subscription unpaused successfully!', 
        { variant: 'success' }
      );
      
      // Reload subscription data
      const updatedData = await getSubscriptionById(id);
      setSubscriptionData(updatedData);
      
      handleClosePauseModal();
    } catch (error) {
      console.error('Failed to pause/unpause subscription:', error);
      enqueueSnackbar(error.message || 'Failed to update subscription', { variant: 'error' });
    } finally {
      setPauseLoading(false);
    }
  }, [pauseStartDate, pauseEndDate, unpauseFromDate, overrideClient, id, pauseModalType, enqueueSnackbar, handleClosePauseModal]);





  // Fetch data if editing existing subscription
  useEffect(() => {
    if (id) {
      const fetchSubscription = async () => {
        try {
          setLoading(true);
          const subscription = await getSubscriptionById(id);

          if (subscription) {
            // Set selected IDs for dependent dropdowns
            setSelectedPlanGroupId(subscription.restaurant_plan_group?.id);

            // Reset form with fetched data
            reset({
              restaurant: {
                id: subscription.restaurant.id,
              },
              customer: {
                id: subscription.customer?.id,
                name: subscription.customer?.name || '',
                phone_number: subscription.customer?.user?.phone_number || '',
                area: {
                  id: subscription.customer?.area?.id || 0,
                },
                block: subscription.customer?.block || '',
                street: subscription.customer?.street || '',
                house: subscription.customer?.house || '',
                avenue: subscription.customer?.avenue || '',
                comment: subscription.customer?.comment || '',
              },
              restaurant_plan_group: {
                id: subscription.restaurant_plan_group?.id || 0,
              },
              restaurant_plan: {
                id: subscription.restaurant_plan?.id || 0,
              },
              delivery_time: {
                id: subscription.delivery_time?.id || 0,
              },
              meal_sizes: {
                id: subscription.meal_sizes?.id,
                protein_size: subscription.meal_sizes?.protein_size,
                carb_size: subscription.meal_sizes?.carb_size,
              },
              meals: subscription.meals?.map(meal => ({
                id: meal.id,
                meal_tag: {
                  id: meal.meal_tag.id,
                },
                plan_tag: {
                  id: meal.plan_tag?.id || 0  // Use plan_tag.id if available
                },
                is_deleted: false,
              })) || [],
              start_date: subscription.start_date || moment().add(1, 'day').format('YYYY-MM-DD'),
              price: subscription.price || '',
              restaurant_note: subscription.restaurant_note || '',
              delivery_note: subscription.delivery_note || '',
              off_days: subscription.off_days?.map(day => day.id) || [],
              period: subscription.period || 30,
              extend_by_days: 0,  // Default for update mode
              is_paid_update_request: false, // Default for update mode
            });

            // Store the original price for validation when requesting payment
            if (subscription.price) {
              setValue('price', subscription.price);
            }

            // Ensure restaurant, plan group, and plan data are loaded
            if (restaurant && subscription.restaurant.id === restaurant.id) {
              // Sync delivery times
              if (restaurant.delivery_times && restaurant.delivery_times.length > 0) {
                const formattedTimes = restaurant.delivery_times.map(dt => ({
                  id: dt.id || 0,
                  start_time: dt.start_time,
                  end_time: dt.end_time,
                }));
                setDeliveryTimes(formattedTimes);
              }
            }

            // Trigger form validation after data is loaded
            methods.trigger('meals');
            console.log('Meals loaded in edit mode:', subscription.meals);

            // Wait for plan group data to be loaded to ensure plan selection
            if (restaurantPlanGroup && subscription.restaurant_plan_group?.id === restaurantPlanGroup.id) {
              // Re-select the plan to ensure it's correctly set after plan group loads
              if (subscription.restaurant_plan?.id) {
                setValue('restaurant_plan.id', subscription.restaurant_plan.id);
              }

              // Sync meal sizes with plan group
              if (restaurantPlanGroup.protein_size && restaurantPlanGroup.carb_size) {
                setValue('meal_sizes.protein_size', subscription.meal_sizes?.protein_size || restaurantPlanGroup.protein_size);
                setValue('meal_sizes.carb_size', subscription.meal_sizes?.carb_size || restaurantPlanGroup.carb_size);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching subscription:', error);
          enqueueSnackbar('Failed to load subscription data', { variant: 'error' });
        } finally {
          setLoading(false);
        }
      };

      fetchSubscription();
    }
  }, [id, reset, enqueueSnackbar, restaurant, restaurantPlanGroup, setValue, methods]);

  // Watch for changes in the selected plan group to update meal sizes
  useEffect(() => {
    if (watchPlanGroupId && watchPlanGroupId !== 0 && restaurantPlanGroup) {
      // Only update if we have actual values and they're not already set
      if (restaurantPlanGroup.protein_size !== undefined && restaurantPlanGroup.carb_size !== undefined) {
        const currentValues = getValues();

        // Set protein size if it's not already set or is set to null/undefined
        if (!currentValues.meal_sizes.protein_size) {
          setValue('meal_sizes.protein_size', restaurantPlanGroup.protein_size);
        }

        // Set carb size if it's not already set or is set to null/undefined
        if (!currentValues.meal_sizes.carb_size) {
          setValue('meal_sizes.carb_size', restaurantPlanGroup.carb_size);
        }
      }
    }
  }, [watchPlanGroupId, restaurantPlanGroup, setValue, getValues]);

  // Sync form fields when restaurant or plan group data loads in edit mode
  useEffect(() => {
    if (!isEdit || !restaurant) return;

    // Sync delivery times from restaurant
    if (restaurant.delivery_times?.length > 0 && restaurant.id === restaurantId) {
      // Format delivery times
      const formattedTimes = restaurant.delivery_times.map(dt => ({
        id: dt.id || 0,
        start_time: dt.start_time,
        end_time: dt.end_time,
      }));
      setDeliveryTimes(formattedTimes);
    }
  }, [isEdit, restaurant, restaurantId]);

  const formatTime = (timeString: string) => moment(timeString, 'HH:mm:ss').format('hh:mm A');

  // Helper to get actual meal tag objects for display
  // const getActiveMealTags = () => {
  //   const meals = getValues('meals') || [];
  //   const activeMealTagIds = meals
  //     // Check is_deleted before mapping
  //     .filter(meal => !meal.is_deleted)
  //     .map(meal => meal.meal_tag.id);
  //
  //   return activeMealTagIds;
  // };

  const { startDate, startDateLoading, startDateEmpty } = useGetStartDate();

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card sx={{ p: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
              <Typography variant="h4">
                {isEdit ? 'Edit Subscription' : 'Create New Subscription'}
              </Typography>

              {/* Pause/Unpause Button - Only show in edit mode for Active subscriptions */}
              {isEdit && subscriptionData && subscriptionData.status === 'active' && (
                <Button
                  variant="outlined"
                  color={subscriptionData.has_valid_paused_dates ? "success" : "warning"}
                  onClick={() => handleOpenPauseModal(subscriptionData.has_valid_paused_dates ? 'unpause' : 'pause')}
                  startIcon={<Iconify icon={subscriptionData.has_valid_paused_dates ? "eva:play-circle-fill" : "eva:pause-circle-fill"} />}
                  sx={{ minWidth: 180 }}
                >
                  {subscriptionData.has_valid_paused_dates ? 'Unpause Subscription' : 'Pause Subscription'}
                </Button>
              )}
            </Stack>

            {/* Customer Information Section */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Customer Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <RHFTextField name="customer.name" label="Customer Name" required disabled={isEdit} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <RHFTextField name="customer.phone_number" label="Phone Number" required disabled={isEdit} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFAutocomplete
                    name="customer.area.id"
                    label="Area"
                    disabled={citiesLoading || isEdit}
                    options={cities || []}
                    getOptionLabel={(option) => {
                      // Handle both string and object options
                      if (typeof option === 'object' && option !== null) {
                        return option.name_en || '';
                      }
                      // Find city by ID for string/number values
                      const city = cities?.find(c => c.id === Number(option));
                      return city ? city.name_en : '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (!option || !value) return false;

                      // Handle case where both are objects with id
                      if (typeof option === 'object' && typeof value === 'object' &&
                          'id' in option && 'id' in value) {
                        return option.id === value.id;
                      }

                      // Handle case where option is object and value is primitive
                      if (typeof option === 'object' && 'id' in option &&
                         (typeof value === 'string' || typeof value === 'number')) {
                        return option.id === Number(value);
                      }

                      return false;
                    }}
                    renderOption={(props, option) => {
                      // Ensure option is an object with id property
                      if (typeof option === 'object' && 'id' in option) {
                        return (
                          <li {...props} key={option.id}>
                            {option.name_en}
                          </li>
                        );
                      }
                      return null;
                    }}
                    onChange={(event, newValue) => {
                      if (newValue && typeof newValue === 'object' && 'id' in newValue) {
                        methods.setValue('customer.area.id', newValue.id);
                      } else {
                        methods.setValue('customer.area.id', 0);
                      }
                    }}
                  />
                  {citiesLoading && <Typography variant="caption">Loading areas...</Typography>}
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField name="customer.block" label="Block" required disabled={isEdit} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField name="customer.street" label="Street" required disabled={isEdit} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField name="customer.avenue" label="Avenue" disabled={isEdit} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField name="customer.house" label="House" required disabled={isEdit} />
                </Grid>

                {/* Customer Comment - Only show in edit mode and for super users */}
                {(isEdit && isSuperUser) && (
                  <Grid item xs={12}>
                    <RHFTextField
                      name="customer.comment"
                      label="Customer Comment"
                      multiline
                      rows={3}
                      disabled={isEdit}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <Divider sx={{ my: 5, borderStyle: 'dashed' }} />

            {/* Plan Selection Section */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Plan Selection
              </Typography>

              <Grid container spacing={3}>
                {/* Plan Group Selection */}
                <Grid item xs={12}>
                  <RHFSelect
                    name="restaurant_plan_group.id"
                    label="Plan Group"
                    required
                    disabled={restaurantPlanGroupsLoading || !restaurantId || isEdit}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value={0}>
                      <em>Select Plan Group</em>
                    </MenuItem>
                    {restaurantPlanGroups?.map((planGroup) => (
                      <MenuItem key={planGroup.id} value={planGroup.id}>
                        {planGroup.special_name_en || planGroup.plan_tag?.name_en}
                        {planGroup.protein_size && planGroup.carb_size &&
                          ` - P:${planGroup.protein_size}g C:${planGroup.carb_size}g`}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                  {isEdit && (
                    <FormHelperText>Plan group cannot be updated</FormHelperText>
                  )}
                  {!restaurantId && !isEdit ? (
                    <FormHelperText>Restaurant ID is missing</FormHelperText>
                  ) : null}
                </Grid>

                {/* Plan Selection */}
                <Grid item xs={12}>
                  <RHFSelect
                    name="restaurant_plan.id"
                    label="Plan"
                    required
                    disabled={restaurantPlanGroupLoading || !watchPlanGroupId || watchPlanGroupId === 0 || isEdit}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value={0}>
                      <em>Select Plan</em>
                    </MenuItem>
                    {restaurantPlanGroup?.plans?.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.meals?.map(meal => meal.name_en).join(', ')}
                        {plan.calories &&
                          ` (${plan.calories.calories || `${plan.calories.calories_from}-${plan.calories.calories_to}`} calories)`}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                  {(!watchPlanGroupId || watchPlanGroupId === 0) && (
                    <FormHelperText>Please select a plan group first</FormHelperText>
                  )}
                  {isEdit && watchPlanGroupId > 0 && (
                     <FormHelperText>Plan cannot be updated</FormHelperText>
                  )}
                </Grid>

                {/* Package Selection - Only show in create mode and if packages are available */}
                {!isEdit && watchPlanId > 0 && (
                  <Grid item xs={12}>
                    {packagesLoading && (
                      <Typography variant="caption">Loading packages...</Typography>
                    )}
                    {!packagesLoading && availablePackages.length > 0 && (
                      <>
                        <RHFSelect
                          name="selected_package"
                          label="Select Package"
                          disabled={packagesLoading}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => {
                            const pkgId = Number(e.target.value);
                            const selectedPkg = availablePackages.find((p) => p.id === pkgId) || null;
                            handlePackageChange(selectedPkg);
                          }}
                          value={selectedPackage?.id || ''}
                        >
                          <MenuItem value="">
                            <em>Select a package</em>
                          </MenuItem>
                          {availablePackages.map((pkg) => (
                            <MenuItem key={pkg.id} value={pkg.id}>
                              {pkg.period} Days - KD {pkg.price}
                              {pkg.discounted_price && ` (Discount: KD ${pkg.discounted_price})`}
                              {pkg.status && pkg.status !== 'active' && ` [${pkg.status.toUpperCase()}]`}
                              {pkg.off_days && pkg.off_days.length > 0 &&
                                ` - Off: ${pkg.off_days.map(day => day.name_en.slice(0, 3)).join(', ')}`}
                            </MenuItem>
                          ))}
                        </RHFSelect>
                        {selectedPackage && (
                          <FormHelperText>
                            Selected package auto-fills Period: {selectedPackage.period} days,
                            Price: KD {selectedPackage.price}
                            {selectedPackage.off_days && selectedPackage.off_days.length > 0 &&
                              `, Off days: ${selectedPackage.off_days.map(d => d.name_en).join(', ')}`}
                          </FormHelperText>
                        )}
                      </>
                    )}
                    {!packagesLoading && availablePackages.length === 0 && watchPlanId > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No package options available for this plan
                      </Typography>
                    )}
                  </Grid>
                )}

                {/* Meal Sizes */}
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="meal_sizes.protein_size"
                    label="Protein Size"
                                        InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0 }}
                    disabled={restaurantPlanGroupLoading}
                  />
                  <FormHelperText>
                    Default protein size for all meals in this subscription
                  </FormHelperText>
                </Grid>

                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="meal_sizes.carb_size"
                    label="Carb Size"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: 0 }}
                    disabled={restaurantPlanGroupLoading}
                  />
                  <FormHelperText>
                    Default carb size for all meals in this subscription
                  </FormHelperText>
                </Grid>

                {/* Delivery Time Selection */}
                <Grid item xs={12} md={6}>
                  <RHFSelect
                    name="delivery_time.id"
                    label="Delivery Time"
                    required
                    disabled={deliveryTimesLoading || !restaurantId}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value={0}>
                      <em>Select Delivery Time</em>
                    </MenuItem>
                    {deliveryTimes?.map((time) => (
                      <MenuItem key={time.id} value={time.id}>
                        {formatTime(time.start_time)} - {formatTime(time.end_time)}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                  {!restaurantId ? (
                    <FormHelperText>Restaurant ID is missing</FormHelperText>
                  ) : null}
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3, borderStyle: 'dashed' }} />

            {/* Meal Sizes */}
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Meals
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Controller
                  name="meals"
                  control={methods.control}
                  render={({ field, fieldState: { error } }) => {
                    // Create unique meal-plan tag pair options
                    const mealPlanOptions = mealPlanPairs.map(pair => ({
                      id: `${pair.meal_tag.id}-${pair.plan_tag.id}`,
                      mealTagId: pair.meal_tag.id,
                      planTagId: pair.plan_tag.id,
                      mealTagName: pair.meal_tag.name_en,
                      planTagName: pair.plan_tag.name_en
                    }));

                    // Get the currently active meal-plan tag pairs
                    const activeMeals = getValues('meals') || [];
                    const activeMealPlanPairs = activeMeals
                      .filter(meal => !meal.is_deleted)
                      .map(meal => ({
                        id: `${meal.meal_tag.id}-${meal.plan_tag.id}`,
                        mealTagId: meal.meal_tag.id,
                        planTagId: meal.plan_tag.id
                      }));

                    return (
                      <Box>
                        <CustomMultiSelect
                          disabled={mealPlanPairsLoading}
                          options={mealPlanOptions.map((option) => option.id)}
                          value={activeMealPlanPairs.map((p) => p.id) || []}
                          onChange={(e, newValue) => {
                            const selectedPairs: MealPlanPair[] = newValue
                              .map((optionId) => {
                                const option = mealPlanOptions.find((opt) => opt.id === optionId);
                                return option
                                  ? { mealTagId: option.mealTagId, planTagId: option.planTagId }
                                  : null;
                              })
                              .filter((pair): pair is MealPlanPair => pair !== null);
                            handleMealTagChange(selectedPairs);
                          }}
                          getOptionLabel={(optionId) => {
                            const foundOption = mealPlanOptions.find((opt) => opt.id === optionId);
                            return foundOption ? foundOption.mealTagName : '';
                          }}
                          getOptionSecondaryLabel={(optionId) => {
                            const foundOption = mealPlanOptions.find((opt) => opt.id === optionId);
                            return foundOption ? `Plan: ${foundOption.planTagName}` : undefined;
                          }}
                          getTagLabel={(optionId) => {
                            const foundOption = mealPlanOptions.find((opt) => opt.id === optionId);
                            return foundOption
                              ? `${foundOption.mealTagName} (${foundOption.planTagName})`
                              : String(optionId);
                          }}
                          label="Meal Types"
                          placeholder={mealPlanPairsLoading ? "Loading meals..." : "Select meals"}
                          error={Boolean(error)}
                          helperText={error?.message || "Select meals and their plan types"}
                          loading={mealPlanPairsLoading}
                          loadingText="Loading meal options..."
                          textFieldProps={{ fullWidth: true, InputLabelProps: { shrink: true } }}
                        />
                      </Box>
                    );
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {/* Subscription Settings Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Subscription Settings
              </Typography>

              <Grid container spacing={3}>
                {/* Start Date */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="start_date"
                    control={methods.control}
                    render={({ field, fieldState: { error } }) => {
                      if (isEdit) {
                        return (
                          <TextField
                            label="Start Date"
                            value={field.value || ''}
                            fullWidth
                            disabled
                            error={Boolean(error)}
                            helperText={error?.message}
                            InputLabelProps={{ shrink: true }}
                          />
                        );
                      }
                      // Generate date options based on backend start date
                      const generateDateOptions = () => {
                        if (!startDate || startDateEmpty) {
                          return [];
                        }

                        const baseDate = new Date(startDate);
                        const options: { date: string, label: string, isOffDay: boolean }[] = [];

                        // Generate 30 days (start date + 29 more days)
                        for (let i = 0; i < 30; i+=1) {
                          const currentDate = new Date(baseDate);
                          currentDate.setDate(baseDate.getDate() + i);

                          // Format the date for display and value
                          const dateString = format(currentDate, 'yyyy-MM-dd');
                          const displayDate = format(currentDate, 'EEE, dd MMM yyyy');

                          // Get day of week (0-6, where 0 is Sunday)
                          const dayOfWeek = currentDate.getDay();
                          // Convert to 1-7 format where 1 is Sunday (to match off_days format)
                          const dayId = dayOfWeek === 0 ? 1 : dayOfWeek + 1;

                          // Check if this day is an off day
                          const offDays = watch('off_days') || [];
                          const isOffDay = offDays.includes(dayId);

                          options.push({
                            date: dateString,
                            label: displayDate,
                            isOffDay
                          });
                        }

                        return options;
                      };

                      const dateOptions = generateDateOptions();

                      // If value not in options, try to reset to first valid date
                      if (field.value && !dateOptions.some(opt => opt.date === field.value)) {
                        const firstValidDate = dateOptions.find(opt => !opt.isOffDay);
                        if (firstValidDate) {
                          // Use timeout to avoid React state update during rendering
                          setTimeout(() => {
                            field.onChange(firstValidDate.date);
                          }, 0);
                        }
                      }

                      return (
                        <FormControl fullWidth error={Boolean(error)}>
                          <InputLabel id="start-date-select-label">Start Date</InputLabel>
                          <Select
                            {...field}
                            labelId="start-date-select-label"
                            label="Start Date"
                            disabled={startDateLoading || isEdit}
                            MenuProps={{
                              PaperProps: {
                                sx: { maxHeight: 400 },
                              },
                            }}
                          >
                            {dateOptions.length === 0 ? (
                              <MenuItem value="">
                                <em>Loading available dates...</em>
                              </MenuItem>
                            ) : (
                              dateOptions.map((option) => (
                                <MenuItem
                                  key={option.date}
                                  value={option.date}
                                  disabled={option.isOffDay}
                                  sx={theme => ({
                                    ...(option.isOffDay && {
                                      color: 'text.disabled',
                                      backgroundColor: 'action.disabledBackground',
                                      '&:hover': {
                                        backgroundColor: 'action.disabledBackground',
                                      },
                                      '&::after': {
                                        content: '" (Off day)"',
                                        marginLeft: '8px',
                                        color: theme.palette.error.main,
                                      },
                                    }),
                                    ...(option.date === format(new Date(), 'yyyy-MM-dd') && {
                                      fontWeight: 'bold',
                                      color: 'primary.main',
                                    }),
                                  })}
                                >
                                  {option.label}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                          <FormHelperText>
                            {error?.message || (startDateLoading ? 'Loading available start dates...' : '')}
                          </FormHelperText>
                        </FormControl>
                      );
                    }}
                  />
                </Grid>

                {/* Period */}
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="period"
                    label="Subscription Period (days)"
                    type="number"
                    required
                    disabled={isEdit}
                    InputProps={{
                      inputProps: { min: 1, max: 90 }
                    }}
                  />
                </Grid>

                {/* Price - Conditionally enabled in update mode */}
                <Grid item xs={12} md={6}>
                  <Controller
                    name="price"
                    control={methods.control}
                    render={({ field, fieldState: { error } }) => {
                      // In edit mode, check if payment is requested
                      const isPaidRequest = isEdit ? watch('is_paid_update_request') : false;

                      return (
                        <TextField
                          {...field}
                          label={isEdit && isPaidRequest ? "Payment Amount" : "Price"}
                          fullWidth
                          required
                          disabled={isEdit && !isPaidRequest}
                          error={Boolean(error)}
                          helperText={
                            error?.message ||
                            (() => {
                              if (isEdit && isPaidRequest) {
                                return 'Enter the payment amount for this update';
                              }
                              if (isEdit) {
                                return 'Enable payment request to modify price';
                              }
                              return 'Subscription price';
                            })()
                          }
                          InputLabelProps={{ shrink: true }}
                          InputProps={{
                            inputProps: { min: 0, step: 0.01 },
                            startAdornment: <Box component="span" sx={{ mr: 1 }}>KD</Box>
                          }}
                        />
                      );
                    }}
                  />
                </Grid>

                {/* Update-specific fields */}
                {isEdit && (
                  <>
                    <Grid item xs={12} md={6}>
                      <RHFSelect
                        name="extend_by_days"
                        label="Extend By"
                        required
                        InputLabelProps={{ shrink: true }}
                      >
                        <MenuItem value={0}>
                          <em>Select Duration</em>
                        </MenuItem>
                        <MenuItem value={1}>1 day</MenuItem>
                        <MenuItem value={2}>2 days</MenuItem>
                        <MenuItem value={3}>3 days</MenuItem>
                        <MenuItem value={4}>4 days</MenuItem>
                        <MenuItem value={5}>5 days</MenuItem>
                        <MenuItem value={6}>6 days</MenuItem>
                        <MenuItem value={7}>7 days</MenuItem>
                      </RHFSelect>
                      <FormHelperText>Number of days to extend the subscription</FormHelperText>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <RHFCheckbox
                        name="is_paid_update_request"
                        label="Request Payment"
                        helperText="Check if this update requires payment"
                      />
                    </Grid>
                  </>
                )}

                {/* Off Days */}
                <Grid item xs={12}>
                  <RHFMultiCheckbox
                    name="off_days"
                    label="Off Days"
                    options={[
                      { label: 'Sunday', value: 1 },
                      { label: 'Monday', value: 2 },
                      { label: 'Tuesday', value: 3 },
                      { label: 'Wednesday', value: 4 },
                      { label: 'Thursday', value: 5 },
                      { label: 'Friday', value: 6 },
                      { label: 'Saturday', value: 7 },
                    ]}
                    row
                    spacing={2}
                  />
                </Grid>

                {/* Restaurant Note */}
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="restaurant_note"
                    label="Restaurant Note"
                    multiline
                    rows={3}
                  />
                </Grid>

                {/* Delivery Note */}
                <Grid item xs={12} md={6}>
                  <RHFTextField
                    name="delivery_note"
                    label="Delivery Note"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Pause Dates Summary - Only show in edit mode if there are paused dates */}
            {isEdit && subscriptionData && subscriptionData.paused_dates && subscriptionData.paused_dates.length > 0 && (
              <>
                <Divider sx={{ my: 3, borderStyle: 'dashed' }} />
                <PauseDatesSummary
                  pausedDates={subscriptionData.paused_dates}
                  offDays={subscriptionData.off_days || []}
                  today={currentDateTime?.date || moment().format('YYYY-MM-DD')}
                />
              </>
            )}

            <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => router.push(paths.dashboard.restaurant.subscription.subscriptions)}
              >
                Cancel
              </Button>

              <LoadingButton
                type="submit"
                variant="contained"
                // Disable if submitting, loading, OR if in edit mode and form is not dirty
                loading={isSubmitting || loading}
                disabled={isSubmitting || loading || (isEdit && !isDirty)}
              >
                {isEdit ? 'Update Subscription' : 'Create Subscription'}
              </LoadingButton>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Payment Link Modal */}
      <Dialog open={showPaymentModal} onClose={() => {
        setShowPaymentModal(false);
        router.push(paths.dashboard.restaurant.subscription.subscriptions);
      }}>
        <DialogTitle>Payment Link</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Use the link below to complete the payment:
          </Typography>
          <TextField
            fullWidth
            value={paymentLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(paymentLink);
                      enqueueSnackbar('Payment link copied to clipboard', { variant: 'success' });
                    }}
                  >
                    <Iconify icon="eva:copy-fill" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowPaymentModal(false);
            router.push(paths.dashboard.restaurant.subscription.subscriptions);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Pause/Unpause Subscription Modal */}
      <Dialog 
        open={showPauseModal} 
        onClose={handleClosePauseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify 
              icon={pauseModalType === 'pause' ? "eva:pause-circle-fill" : "eva:play-circle-fill"} 
              sx={{ color: pauseModalType === 'pause' ? 'warning.main' : 'success.main' }}
            />
            <Typography variant="h6">
              {pauseModalType === 'pause' ? 'Pause Subscription' : 'Unpause Subscription'}
            </Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent>
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              {pauseModalType === 'pause' 
                ? 'Please select the start and end dates for pausing the subscription.'
                : 'Please select the date from which you want to unpause the subscription.'
              }
            </Typography>

            {pauseModalType === 'pause' ? (
              <>
                <CustomPauseUnpauseDateSelect
                  label="Start Date"
                  value={pauseStartDate}
                  onChange={(dateStr) => {
                    setPauseStartDate(dateStr);
                    setPauseStartDateError('');
                    // Clear end date when start date changes to force user to reselect
                    if (pauseEndDate && moment(dateStr).isAfter(pauseEndDate)) {
                      setPauseEndDate('');
                    }
                  }}
                  required
                  error={Boolean(pauseStartDateError)}
                  helperText={pauseStartDateError || 'Select the start date for pausing the subscription'}
                  serverDate={currentDateTime?.date}
                  offDays={subscriptionData?.off_days?.map(day => day.id) || []}
                  maxDays={30}
                  defaultDaysOffset={(() => {
                    // Calculate smart default offset that avoids off days
                    const today = moment(currentDateTime?.date || moment().format('YYYY-MM-DD'));
                    const subscriptionStart = moment(subscriptionData?.start_date || today.format('YYYY-MM-DD'));
                    const baseDate = today.isAfter(subscriptionStart) ? today : subscriptionStart;
                    const offDayIds = subscriptionData?.off_days?.map(day => day.id) || [];
                    
                    // Start checking from 3 days offset
                    let offset = 0;
                    let attempts = 0;
                    const maxAttempts = 10; // Prevent infinite loop
                    
                    while (attempts < maxAttempts) {
                      const checkDate = baseDate.clone().add(offset, 'days');
                      const dayOfWeek = checkDate.day();
                      const dayId = dayOfWeek === 0 ? 1 : dayOfWeek + 1;
                      
                      // If this day is not an off day, use this offset
                      if (!offDayIds.includes(dayId)) {
                        return offset;
                      }
                      
                      offset += 1;
                      attempts += 1;
                    }
                    
                    // Fallback to 3 if we can't find a good day
                    return 0;
                  })()}
                  showLockedChips
                  showOffDayChips
                  // Limit start date to max of today or subscription start date
                  minDate={(() => {
                    const today = currentDateTime?.date || moment().format('YYYY-MM-DD');
                    const subscriptionStart = subscriptionData?.start_date;
                    
                    if (!subscriptionStart) return today;
                    
                    // Return the later date between today and subscription start
                    return moment(today).isAfter(subscriptionStart) ? today : subscriptionStart;
                  })()}
                />

                <CustomPauseUnpauseDateSelect
                  label="End Date"
                  value={pauseEndDate}
                  onChange={setPauseEndDate}
                  required
                  disabled={!pauseStartDate}
                  error={!pauseStartDate && pauseModalType === 'pause'}
                  helperText={
                    !pauseStartDate 
                      ? 'Please select a start date first' 
                      : 'Select the end date for pausing the subscription'
                  }
                  startDate={pauseStartDate}
                  serverDate={currentDateTime?.date}
                  offDays={subscriptionData?.off_days?.map(day => day.id) || []}
                  maxDays={30}
                  defaultDaysOffset={-1} // Don't auto-select end date, let user choose
                  showLockedChips
                  showOffDayChips
                />
              </>
            ) : (
              <>
                <CustomPauseUnpauseDateSelect
                  label="From Date"
                  value={unpauseFromDate}
                  onChange={setUnpauseFromDate}
                  required
                  disabled={currentDateTimeLoading}
                  helperText={
                    currentDateTimeLoading 
                      ? 'Loading available dates...' 
                      : 'Select a paused date to unpause from.'
                  }
                  serverDate={currentDateTime?.date}
                  offDays={subscriptionData?.off_days?.map(day => day.id) || []}
                  pausedDates={subscriptionData?.paused_dates || []}
                  subscriptionStartDate={subscriptionData?.start_date}
                  // For unpause, limit to latest paused date instead of arbitrary maxDays
                  maxDate={(() => {
                    const pausedDates = subscriptionData?.paused_dates || [];
                    if (pausedDates.length === 0) return undefined;
                    
                    // Find the latest paused date
                    const latestPausedDate = pausedDates.reduce((latest, current) => {
                      return moment(current.date).isAfter(latest.date) ? current : latest;
                    });
                    
                    return latestPausedDate.date;
                  })()}
                  onlyAllowPausedDates
                  showPlatformChips
                  showOffDayChips
                  showLockedChips
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={overrideClient}
                      onChange={(e) => setOverrideClient(e.target.checked)}
                      color="warning"
                    />
                  }
                  label="Override Client"
                />
                
                {overrideClient && (
                  <Alert severity="warning">
                    <Typography variant="body2">
                      This will forcefully unpause all paused days, including the ones paused by the client user.
                    </Typography>
                  </Alert>
                )}
              </>
            )}

            <Alert severity={pauseModalType === 'pause' ? 'warning' : 'info'}>
              <AlertTitle>
                {pauseModalType === 'pause' ? 'What happens next?' : 'What happens when you unpause?'}
              </AlertTitle>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {pauseModalType === 'pause' ? (
                  <>
                    <li>Subscription will be extended automatically</li>
                    <li>Customer will get notified via SMS</li>
                    <li>Customer will not be able to choose meals for the paused Dates</li>
                    <li>Customer&apos;s chosen meals for effective dates will be removed from system</li>
                    <li>Customer will not be able to unpause dates paused from this dashboard</li>
                  </>
                ) : (
                  <>
                    <li>Subscription end date will be adjusted automatically</li>
                    <li>Customer will get notified via SMS</li>
                    <li>Customer will be able to choose meals for the unpaused Dates again</li>
                  </>
                )}
              </Box>
            </Alert>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePauseModal} disabled={pauseLoading}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleConfirmPause}
            loading={pauseLoading}
            variant="contained"
            color={pauseModalType === 'pause' ? 'warning' : 'success'}
            disabled={
              pauseModalType === 'pause' 
                ? !pauseStartDate || !pauseEndDate
                : !unpauseFromDate || currentDateTimeLoading
            }
          >
            {pauseModalType === 'pause' ? 'Confirm Pause' : 'Confirm Unpause'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
